import React, { useState, useEffect, useMemo, useRef } from 'react';
import { IcPrinter, IcMail, IcPlus, IcTrash, IcScanBarcode, IcPackageOpen, IcFileText } from '../../theme/icons';
import Swal from '../../theme/swal';
import { format } from 'date-fns';
import devisService from '../../services/devisService';
import productService from '../../services/productService';
import { useCompany } from '../../context/CompanyContext';
import { printDocument } from '../../desktop/engine';
import KI from '../../theme/palette';
import { filterSearch } from '../../utils/search';

// Une seule page : le devis est une FEUILLE que l'on remplit comme sur papier.
// Dessin = la feuille imprimée du premier devis (société à gauche, titre + date
// à droite, encadré client, tableau bordé à en-tête gris, totaux sous « Prix
// unit. »). TAILLE de page = celle de la facture (/facturation/detail/:id :
// feuille `max-w-4xl` centrée, marges p-8 / md:p-12) — la taille seulement, pas
// le dessin. Tout se modifie sur place ; un champ laissé vide n'est pas imprimé.
// La feuille peut aussi s'imprimer VIDE, pour être remplie à la main.

// Textes de départ d'une feuille vierge — chacun se modifie sur la feuille.
const DEFAULT_TEXTS = {
  title: 'Devis',
  footer: 'Merci pour votre confiance',
  sign1: 'Signature et cachet',
  sign2: 'Signature du client',
};

const DRAFT_KEY = 'kipara.devis.draft';
const round3 = (v) => Math.round((parseFloat(v) || 0) * 1000) / 1000;
const money = (v) => round3(v).toFixed(3);
const clampPct = (v) => Math.min(Math.max(parseFloat(v) || 0, 0), 100);
const lineTotal = (l) => round3((parseFloat(l.price) || 0) * (parseInt(l.qty, 10) || 0));

let lineSeq = 0;
const newLine = (patch = {}) => ({ key: `l${Date.now()}-${++lineSeq}`, product: null, name: '', price: '', qty: 1, ...patch });

// title / footer / sign1 / sign2 à `null` = texte de départ ; une valeur (même
// vide) = ce qui a été écrit sur cette feuille.
const blankSheet = () => ({
  id: null,
  title: null,
  date: format(new Date(), 'dd/MM/yyyy'),
  clientName: '',
  clientPhone: '',
  clientEmail: '',
  lines: [],
  remise: '',
  footer: null,
  sign1: null,
  sign2: null,
});

const loadDraft = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
    if (raw && Array.isArray(raw.lines)) return { ...blankSheet(), ...raw };
  } catch (e) { /* brouillon illisible : feuille vierge */ }
  return blankSheet();
};

// Champ « papier » : se lit comme du texte, se modifie au clic. Focus = règle
// unique des champs (bordure + anneau 1px encre).
const paper = 'w-full bg-transparent border border-transparent rounded px-1.5 py-1 hover:border-gray-300 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 focus:outline-none placeholder:text-gray-300';
const th = 'border border-gray-200 bg-gray-100 px-2 py-1.5 text-left font-bold text-gray-900';
const td = 'border border-gray-200 px-2 py-1';
// La liste garde la place de MIN_ROWS produits même avec une seule ligne (les
// colonnes descendent jusqu'au trait gras du bas, comme une facture grossiste).
const MIN_ROWS = 15;

const DevisPage = () => {
  const { settings: company } = useCompany();
  const [sheet, setSheet] = useState(loadDraft);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProducts, setShowProducts] = useState(false);
  const [busy, setBusy] = useState(false);
  const printAfterRender = useRef(false);
  const focusLastLine = useRef(false);

  const set = (patch) => setSheet((s) => ({ ...s, ...patch }));
  const setLine = (key, patch) => setSheet((s) => ({ ...s, lines: s.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
  const removeLine = (key) => setSheet((s) => ({ ...s, lines: s.lines.filter((l) => l.key !== key) }));
  // Texte de la feuille s'il a été écrit ici, sinon le texte de départ.
  const text = (k) => (sheet[k] == null ? DEFAULT_TEXTS[k] : sheet[k]);

  useEffect(() => {
    productService.getAllProducts().then(setProducts).catch(() => { /* la feuille reste utilisable à la main */ });
  }, []);

  // Brouillon gardé sur ce poste : un F5 ne fait pas perdre la feuille.
  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(sheet)); } catch (e) { /* stockage indisponible */ }
  }, [sheet]);

  useEffect(() => {
    if (focusLastLine.current) {
      focusLastLine.current = false;
      const inputs = document.querySelectorAll('#devis-sheet input[aria-label="Produit"]');
      if (inputs.length) inputs[inputs.length - 1].focus();
    }
    if (printAfterRender.current) {
      printAfterRender.current = false;
      printDocument('a4');
    }
  });

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return [];
    // Règle Ki Para (utils/search.js) : début de mot, code « contient ».
    return filterSearch(products, productSearch, { text: (p) => p.name, codes: (p) => [p.code] }).slice(0, 30);
  }, [products, productSearch]);

  const addProduct = (p) => {
    setSheet((s) => ({ ...s, lines: [...s.lines, newLine({ product: p.id, name: p.name, price: round3(p.price).toFixed(3) })] }));
    setProductSearch('');
    setShowProducts(false);
  };

  // Ligne écrite à la main — ce qui est tapé dans la recherche devient son nom.
  const addFreeLine = () => {
    const name = productSearch.trim();
    setSheet((s) => ({ ...s, lines: [...s.lines, newLine({ name })] }));
    setProductSearch('');
    setShowProducts(false);
    focusLastLine.current = !name;
  };

  const written = sheet.lines.filter((l) => (l.name || '').trim());
  const subTotal = round3(written.reduce((s, l) => s + lineTotal(l), 0));
  const remisePct = clampPct(sheet.remise);
  const discount = round3(subTotal * remisePct / 100);
  const total = round3(subTotal - discount);

  // Enregistrement silencieux (trace + envoi e-mail par le serveur).
  const persist = async () => {
    const data = await devisService.saveDevis(sheet.id, {
      customer_name: sheet.clientName,
      customer_phone: sheet.clientPhone,
      customer_email: sheet.clientEmail,
      remise: remisePct,
      document: { title: text('title'), date: sheet.date, footer: text('footer'), sign1: text('sign1'), sign2: text('sign2') },
      items: written.map((l) => ({
        product: l.product || null,
        product_name: l.name.trim(),
        quantity: parseInt(l.qty, 10) || 0,
        unit_price: round3(l.price),
      })),
    });
    set({ id: data.id });
    return data;
  };

  const needLines = () => {
    if (written.length > 0) return false;
    Swal.fire({ icon: 'info', title: 'Devis vide', text: 'Ajoutez au moins une ligne avant d\'envoyer le devis.', confirmButtonColor: KI.ink });
    return true;
  };

  const doPrint = async () => {
    setBusy(true);
    try {
      if (written.length > 0) await persist(); // une feuille vide s'imprime sans être enregistrée
    } catch (e) {
      // Le papier d'abord : hors ligne, on imprime quand même.
      console.error('Devis non enregistré :', e);
    } finally {
      setBusy(false);
    }
    printAfterRender.current = true;
    setSheet((s) => ({ ...s })); // déclenche le rendu qui lance l'impression
  };

  const mailtoFallback = (to) => {
    const nom = (company && company.nom) || 'Kipara';
    const body = [
      'Bonjour,', '',
      `Voici votre ${(text('title') || 'devis').toLowerCase()} du ${sheet.date} :`, '',
      ...written.map((l) => `- ${l.name.trim()} — ${parseInt(l.qty, 10) || 0} × ${money(l.price)} DT = ${money(lineTotal(l))} DT`),
      '',
      `Sous-total : ${money(subTotal)} DT`, `Remise (${remisePct}%) : -${money(discount)} DT`,
      `Total TTC : ${money(total)} DT`, '',
      text('footer') || '', '', nom,
    ].join('\n');
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(`${text('title') || 'Devis'} — ${nom}`)}&body=${encodeURIComponent(body)}`;
  };

  const doEmail = async () => {
    if (needLines()) return;
    const ask = await Swal.fire({
      title: 'Envoyer le devis par e-mail',
      input: 'email',
      inputLabel: 'Adresse e-mail du client',
      inputValue: sheet.clientEmail || '',
      inputPlaceholder: 'client@exemple.com',
      validationMessage: 'Adresse e-mail invalide',
      showCancelButton: true,
      confirmButtonText: 'Envoyer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: KI.ink,
    });
    if (!ask.isConfirmed || !ask.value) return;
    const to = ask.value.trim();
    set({ clientEmail: to });
    setBusy(true);
    try {
      let id = sheet.id;
      try { id = (await persist()).id; } catch (e) { console.error('Devis non enregistré :', e); }
      let configured = false;
      if (id) {
        try { configured = (await devisService.getEmailStatus()).configured; } catch (e) { configured = false; }
      }
      if (configured) {
        await devisService.sendEmail(id, to);
        Swal.fire({ icon: 'success', title: 'Devis envoyé', text: to, timer: 2200, showConfirmButton: false });
      } else {
        // Serveur sans SMTP : le message s'ouvre, déjà rédigé, dans l'application e-mail du poste.
        mailtoFallback(to);
      }
    } catch (e) {
      Swal.fire('Erreur', e.response?.data?.detail || 'Échec de l\'envoi du devis', 'error');
    } finally {
      setBusy(false);
    }
  };

  const doReset = async () => {
    const c = await Swal.fire({
      title: 'Commencer une feuille vierge ?',
      text: 'Ce qui est écrit sur cette feuille sera effacé.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, feuille vierge',
      cancelButtonText: 'Non',
      confirmButtonColor: KI.ink,
    });
    if (c.isConfirmed) setSheet(blankSheet());
  };

  const isBlank = sheet.lines.length === 0 && !sheet.clientName && !sheet.clientPhone && !sheet.clientEmail;
  // Feuille sans aucune ligne : elle s'imprime comme un formulaire à remplir à
  // la main (encadré client vide, montants laissés en blanc).
  const empty = written.length === 0;
  const hide = (v) => (String(v || '').trim() ? '' : 'print:hidden');
  const hideClient = (v) => (empty ? '' : hide(v));
  const amount = (v) => <span className={empty ? 'devis-blank' : ''}>{money(v)}</span>;
  const phones = ((company && company.telephones) || []).filter((t) => t);

  return (
    <div className="w-full px-4 pt-2 pb-4 print:bg-white print:p-0">
      {/* Nom de page = chip actif `.tab-chip-on` (page ouverte depuis le menu ⚙). */}
      <div className="mb-4 flex no-print">
        <h1 className="tab-chip-on cursor-default">
          <IcFileText size={13} /> Devis
        </h1>
      </div>

      {/* Barre d'outils (hors document imprimé) — même largeur que la feuille */}
      <div className="no-print max-w-4xl mx-auto mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-h-[2.75rem]">
          {!isBlank && (
            <button type="button" onClick={doReset} className="link-reset gap-1.5 px-3 py-1.5 text-sm">
              Feuille vierge
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={doEmail} disabled={busy} className="btn-s py-2 px-6 transition-all">
            <IcMail className="mr-2" />
            Envoyer par e-mail
          </button>
          <button type="button" onClick={doPrint} disabled={busy} className="btn-s py-2 px-6 transition-all">
            <IcPrinter className="mr-2" />
            Imprimer le devis
          </button>
        </div>
      </div>

      {/* ===== La feuille — taille de la feuille de facture ===== */}
      <div id="devis-sheet" className="max-w-4xl mx-auto bg-white border border-gray-200 p-8 md:p-12 print:border-0 text-[13px] text-gray-900">
        {/* Société à gauche · titre + date à droite */}
        <div className="flex flex-col sm:flex-row print:flex-row justify-between items-start gap-6 mb-6">
          <div className="text-gray-700 whitespace-pre-line">
            <p className="text-lg font-bold text-gray-900">{(company && company.nom) || 'Kipara'}</p>
            {company && company.adresse && <p>{company.adresse}</p>}
            {phones.length > 0 && <p>Tél : {phones.join(' · ')}</p>}
            {company && company.matricule_fiscale && <p>MF : {company.matricule_fiscale}</p>}
          </div>
          <div className="w-full sm:w-64 print:w-64">
            <input
              type="text"
              value={text('title')}
              onChange={(e) => set({ title: e.target.value })}
              aria-label="Titre du document"
              placeholder="Devis"
              className={`${paper} text-xl font-bold sm:text-right print:text-right ${hide(text('title'))}`}
            />
            <label className={`flex items-center sm:justify-end print:justify-end gap-1 text-gray-700 ${hide(sheet.date)}`}>
              <span className="whitespace-nowrap">Date :</span>
              <input
                type="text"
                value={sheet.date}
                onChange={(e) => set({ date: e.target.value })}
                placeholder="jj/mm/aaaa"
                className={`${paper} !w-28 sm:text-right print:text-right`}
              />
            </label>
          </div>
        </div>

        {/* Encadré client (imprimé vide sur une feuille vide, pour écrire le nom à la main) */}
        <div className={`border border-gray-200 px-3 py-2.5 mb-5 ${empty ? 'print:min-h-[4.5rem]' : ''} ${hideClient(`${sheet.clientName}${sheet.clientPhone}${sheet.clientEmail}`)}`}>
          <p className="text-[11px] uppercase tracking-[0.04em] text-gray-500 mb-0.5 px-1.5">Client</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 print:grid-cols-3 gap-x-3">
            <input type="text" value={sheet.clientName} onChange={(e) => set({ clientName: e.target.value })} placeholder="Nom du client" aria-label="Nom du client" className={`${paper} font-bold ${hide(sheet.clientName)}`} />
            <input type="tel" value={sheet.clientPhone} onChange={(e) => set({ clientPhone: e.target.value })} placeholder="Téléphone" aria-label="Téléphone du client" className={`${paper} ${hide(sheet.clientPhone)}`} />
            <input type="email" value={sheet.clientEmail} onChange={(e) => set({ clientEmail: e.target.value })} placeholder="E-mail" aria-label="E-mail du client" className={`${paper} ${hide(sheet.clientEmail)}`} />
          </div>
        </div>

        {/* Recherche AU-DESSUS du tableau : au clic la liste s'ouvre, « Ajouter une ligne » en pied
            de liste (même grammaire que « Ajouter un nouveau produit » de la facture). */}
        <div className="no-print relative mb-3">
          <label className="sr-only" htmlFor="devis-product-search">Rechercher un produit ou ajouter une ligne</label>
          <IcScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={13} />
          <input
            id="devis-product-search"
            type="text"
            value={productSearch}
            onChange={(e) => { setProductSearch(e.target.value); setShowProducts(true); }}
            onClick={() => setShowProducts(true)}
            onFocus={() => setShowProducts(true)}
            onBlur={() => setTimeout(() => setShowProducts(false), 150)}
            className="inp w-full px-3 py-2.5 pl-9 text-sm text-gray-900"
            placeholder="Rechercher un produit par nom ou code barre..."
          />
          {showProducts && (
            <div className="ac-list mt-1 rounded-lg max-h-72 overflow-auto">
              {filteredProducts.length > 0 ? filteredProducts.map((p) => (
                <div key={p.id} onMouseDown={(e) => e.preventDefault()} className="ac-row gap-3" onClick={() => addProduct(p)}>
                  <span className="h-9 w-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                    {p.image
                      ? <img src={p.image} alt="" className="h-full w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      : <IcPackageOpen className="text-gray-300" size={14} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="ac-name truncate">{p.name}</div>
                    <div className="ac-sub text-sm flex justify-between">
                      <span>Code: {p.code || 'N/A'}</span>
                      <span>Prix: {(parseFloat(p.price) || 0).toFixed(3)} DT</span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="px-4 py-2.5 text-gray-500 text-sm">
                  {productSearch.trim() ? 'Aucun produit trouvé' : 'Tapez un nom ou un code barre pour rechercher…'}
                </div>
              )}
              <div
                role="button"
                tabIndex={0}
                onMouseDown={(e) => e.preventDefault()}
                onClick={addFreeLine}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addFreeLine(); } }}
                className="px-4 py-2.5 cursor-pointer btn-add-block"
              >
                <IcPlus className="mr-2" />
                Ajouter une ligne
              </div>
            </div>
          )}
        </div>

        {/* Lignes — tableau bordé, en-tête gris ; place pour MIN_ROWS produits,
            trait du bas en gras */}
        <div>
          <table id="devis-lines" className="w-full border-collapse">
            <thead>
              <tr>
                <th className={`${th} w-10`}>#</th>
                <th className={th}>Produit</th>
                <th className={`${th} w-20 !text-right`}>Qté</th>
                <th className={`${th} w-36 !text-right`}>Prix unit. (DT)</th>
                <th className={`${th} w-32 !text-right`}>Total (DT)</th>
                <th className="w-0 p-0 no-print" aria-label="Retirer" />
              </tr>
            </thead>
            <tbody>
              {sheet.lines.map((l) => (
                <tr key={l.key} className={hide(l.name)}>
                  <td className={`${td} text-gray-700`}>{written.indexOf(l) + 1 || ''}</td>
                  <td className={`${td} min-w-[12rem]`}>
                    <input type="text" value={l.name} onChange={(e) => setLine(l.key, { name: e.target.value })} placeholder="Écrire un produit…" aria-label="Produit" className={paper} />
                  </td>
                  <td className={td}>
                    <input type="number" min="0" step="1" inputMode="numeric" value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} aria-label="Quantité" className={`${paper} text-right tabular-nums`} />
                  </td>
                  <td className={td}>
                    <input
                      type="number" min="0" step="0.001" inputMode="decimal"
                      value={l.price}
                      onChange={(e) => setLine(l.key, { price: e.target.value })}
                      onBlur={(e) => { if (e.target.value !== '') setLine(l.key, { price: round3(e.target.value).toFixed(3) }); }}
                      placeholder="0.000"
                      aria-label="Prix unitaire"
                      className={`${paper} text-right tabular-nums`}
                    />
                  </td>
                  <td className={`${td} text-right tabular-nums whitespace-nowrap`}>{money(lineTotal(l))}</td>
                  <td className="w-0 p-0 relative no-print">
                    <button
                      type="button"
                      onClick={() => removeLine(l.key)}
                      title="Retirer la ligne"
                      aria-label="Retirer la ligne"
                      className="absolute left-0.5 top-1/2 -translate-y-1/2 p-2 rounded-xl inline-flex items-center justify-center text-red-600 hover:bg-red-50"
                    >
                      <IcTrash />
                    </button>
                  </td>
                </tr>
              ))}
              {/* Place libre : les colonnes continuent jusqu'au trait gras du bas. */}
              <tr aria-hidden="true">
                {[0, 1, 2, 3, 4].map((c) => (
                  <td
                    key={c}
                    className="border-x border-gray-200 border-b-2 border-b-gray-900 p-0"
                    style={{ height: `calc(var(--devis-row) * ${Math.max(0, MIN_ROWS - written.length)})` }}
                  />
                ))}
                <td className="w-0 p-0 no-print" />
              </tr>
            </tbody>
          </table>
        </div>

        {/* Totaux — toujours les 3 lignes (même sans remise), sous le tableau, à
            droite : commencent sous « Prix unit. » (mêmes largeurs que les 2 dernières colonnes). */}
        <div className="flex justify-end">
          <table className="border-collapse">
            <tbody>
              <tr>
                <td className={`${td} border-t-0 w-36`}>Sous-total</td>
                <td className={`${td} border-t-0 w-32 text-right tabular-nums`}>{amount(subTotal)}</td>
              </tr>
              <tr>
                <td className={`${td} w-36`}>
                  <label className="inline-flex items-center gap-1" htmlFor="devis-remise">
                    Remise
                    <input
                      id="devis-remise"
                      type="number" min="0" max="100" step="1" inputMode="decimal"
                      value={sheet.remise}
                      onChange={(e) => set({ remise: e.target.value })}
                      placeholder="0"
                      className={`${paper} !w-12 text-right tabular-nums`}
                    />
                    %
                  </label>
                </td>
                <td className={`${td} w-32 text-right tabular-nums`}>{empty ? amount(0) : <>−{money(discount)}</>}</td>
              </tr>
              <tr className="font-bold">
                <td className={`${td} border-t-0 w-36`}>Total TTC</td>
                <td className={`${td} border-t-0 w-32 text-right tabular-nums`}>{amount(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Signatures — client à gauche, cachet à droite ; libellé + place libre,
            sans case. Libellé vidé = non imprimé. */}
        <div className={`mt-2 grid grid-cols-1 sm:grid-cols-2 print:grid-cols-2 gap-6 ${hide(`${text('sign1')}${text('sign2')}`)}`}>
          {['sign2', 'sign1'].map((k) => (
            <div key={k} className={`min-h-[5.5rem] ${hide(text(k))}`}>
              <input
                type="text"
                value={text(k)}
                onChange={(e) => set({ [k]: e.target.value })}
                placeholder="Libellé de signature (vide = non imprimé)"
                aria-label="Libellé de signature"
                className={`${paper} font-bold text-gray-700 ${k === 'sign1' ? 'sm:text-right print:text-right' : ''}`}
              />
            </div>
          ))}
        </div>

        {/* Pied — centré ; à l'impression il est posé tout en bas du papier
            (là où le navigateur écrivait l'adresse du site). */}
        <div id="devis-foot" className={`mt-6 ${hide(text('footer'))}`}>
          <textarea
            rows={1}
            value={text('footer')}
            onChange={(e) => set({ footer: e.target.value })}
            placeholder="Texte de bas de page"
            aria-label="Texte de bas de page"
            className={`${paper} text-xs text-gray-500 text-center resize-none`}
          />
        </div>
      </div>

      {/* Impression : la feuille seule (ni en-tête du site, ni boutons, ni champs
          vides), A4 portrait, une page. */}
      <style>{`
        #devis-sheet { --devis-row: 2.25rem; }
        #devis-lines tbody td { height: var(--devis-row); }
        #devis-sheet td input { padding-top: 0; padding-bottom: 0; }
        #devis-sheet input[type=number] { -moz-appearance: textfield; appearance: textfield; }
        #devis-sheet input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
        @media print {
          .no-print { display: none !important; }
          body * { visibility: hidden; }
          #devis-sheet, #devis-sheet * { visibility: visible; }
          /* Feuille vide : montants laissés en blanc, à remplir à la main. */
          #devis-sheet .devis-blank { visibility: hidden; }
          /* @page margin 0 : le navigateur n'a plus de marge où écrire l'adresse du
             site, la date et le titre ; la marge du papier est portée par la feuille. */
          #devis-sheet { --devis-row: 2.2rem; position: absolute; left: 0; top: 0; width: 100%; max-width: 100% !important; margin: 0 !important; padding: 1cm 1cm 0; box-sizing: border-box; }
          #devis-sheet input, #devis-sheet textarea { border-color: transparent !important; box-shadow: none !important; }
          #devis-sheet input::placeholder, #devis-sheet textarea::placeholder { color: transparent; }
          /* Sans remise, la ligne s'imprime quand même : « Remise 0 % ». */
          #devis-sheet #devis-remise::placeholder { color: inherit; opacity: 1; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          #devis-foot { position: fixed; left: 1cm; right: 1cm; bottom: 0.6cm; margin: 0; }
          @page { size: A4 portrait; margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default DevisPage;
